/**
 * JADE TUNNEL — cartridge palette.
 *
 * Vaporwave jungle at dusk. The Pitfall heritage suggests bright primary
 * jungle (Activision green / Atari yellow), but JADE TUNNEL is filtered
 * through the cabinet's vaporwave-CRT aesthetic — magenta canopy,
 * neon-cyan vines, hot-orange treasure glow, dusk sky.
 *
 * The "jade" in the name is the vine + treasure cyan-green. The
 * "tunnel" is the cave subsystem, which renders against a deeper
 * midnight-magenta background to feel like a separate world.
 */

// ---- Surface (jungle) ----
export const SKY_TOP = '#0a0418';        // deep night-magenta at canvas top
export const SKY_HORIZON = '#7a1850';    // hot dusk magenta near the horizon
export const SUN_BODY = '#ff3088';       // hot-pink dying sun
export const SUN_GLOW = '#ff80c8';       // sun outer halo

export const JUNGLE_BG = '#1a0830';      // jungle "fog" behind everything
export const CANOPY = '#5a1054';         // tree canopy magenta
export const CANOPY_DARK = '#2a0820';    // deeper canopy shadow
export const TRUNK = '#3a1a48';          // tree trunks
export const TRUNK_DARK = '#1a0820';     // trunk shadow

export const GROUND_TOP = '#2a4030';     // dusk-jungle ground (muted teal-green)
export const GROUND_BOTTOM = '#0e1810';  // deeper ground under feet
export const GROUND_EDGE = '#1a3020';    // pit edge

// Vines — the cartridge's hero color. Neon cyan against the magenta
// canopy is the cartridge's signature read.
export const VINE = '#00f0c8';
export const VINE_HIGHLIGHT = '#a0fff0';
export const VINE_DARK = '#008070';

// ---- Caves ----
export const CAVE_BG = '#06020a';        // near-black with a magenta tint
export const CAVE_WALL = '#2a1438';      // cave wall lit by ambient
export const CAVE_WALL_DARK = '#100620'; // deeper cave shadow
export const CAVE_FLOOR = '#1a0a28';
export const LADDER = '#00f0c8';         // ladders glow vine-cyan
export const LADDER_RUNG = '#a0fff0';

// ---- Hazards ----
export const PIT = '#000000';            // bottomless black
export const GATOR_BODY = '#3fa050';     // muted dusk-jungle green
export const GATOR_BACK = '#1f5028';
export const GATOR_EYE = '#ffea00';
export const GATOR_TEETH = '#fff5e0';

export const SNAKE_BODY = '#80ff60';     // mint-green snake — pops against canopy
export const SNAKE_DARK = '#308820';

export const SCORPION_BODY = '#ff3030';  // blood-red scorpion (danger read)
export const SCORPION_DARK = '#8a1010';

export const QUICKSAND = '#a08040';      // muddy ochre
export const QUICKSAND_DARK = '#604820';

export const TAR = '#1a0a14';            // deep almost-black, faintly purple
export const TAR_HIGHLIGHT = '#3a1838';  // reflective sheen on tar

export const LOG_BODY = '#6a3818';       // rolling-log brown
export const LOG_DARK = '#3a1c08';
export const LOG_RING = '#2a1408';

export const BAT_BODY = '#1a0830';       // cave bat — same as jungle bg so it
                                         // reads as a "shadow with wings"
export const BAT_EYE = '#ff5050';        // eye glow only

// ---- Loot ----
export const COIN_SILVER = '#c0c8d0';
export const COIN_GOLD = '#ffea00';
export const RING_DIAMOND = '#a0f0ff';   // diamond ring blue-white
export const IDOL_BODY = '#00f0c8';      // jade idol — the cartridge's namesake
export const IDOL_GLOW = '#80ffe0';
export const IDOL_EYE = '#ff3088';       // hot-pink eye gem

// ---- Player ----
// Explorer in vaporwave gear — neon-orange jumpsuit to pop against the
// magenta canopy + green ground.
export const PLAYER_BODY = '#ff8030';
export const PLAYER_OUTLINE = '#8a3010';
export const PLAYER_HEAD = '#ffdba8';    // skin tone
export const PLAYER_HAT = '#c04020';     // pith-helmet rust

// ---- HUD ----
export const HUD_LABEL = '#7af3c8';      // cabinet-default cyan label
export const HUD_SCORE = '#ffea00';
export const HUD_LIVES = '#ff8030';      // matches player body
export const HUD_TIME_OK = '#80ff60';    // sun far from horizon
export const HUD_TIME_WARN = '#ffea00';  // sun close
export const HUD_TIME_CRIT = '#ff3088';  // sun almost touching horizon

// ---- Effects ----
export const FLASH_HIT = '#ffffff';
export const FLASH_PICKUP = '#a0fff0';

/**
 * Tokens the cabinet samples for marquee tinting while this cartridge
 * is loaded. First entry is `paletteHint[0]`, used everywhere the
 * cabinet needs a single "this is the cartridge's color" pick.
 */
export const PALETTE_HINT: readonly string[] = [
  VINE,         // jade — the cartridge's namesake color
  PLAYER_BODY,  // explorer orange
  SUN_BODY,     // dusk pink
  CANOPY,       // magenta canopy
];
